import { useState, useMemo } from 'react';
import { Bell } from 'lucide-react';
import { PullToRefresh } from '../../components/PullToRefresh';
import AlertCard from '../../components/supervisor/AlertCard';
import { useTeamOverview } from '../../hooks/use-team-overview';
import type { TeamAlert } from '../../hooks/use-team-overview';

type FilterTab = 'open' | 'all';

export default function SupervisorAlerts() {
  const [tab, setTab] = useState<FilterTab>('open');
  const { data, isLoading, refetch } = useTeamOverview();
  const handleRefresh = async () => { await refetch(); };

  // Build a phone lookup map from promoters
  const phoneMap = useMemo(() => {
    if (!data) return {} as Record<string, string>;
    return data.promoters.reduce<Record<string, string>>((acc, p) => {
      if (p.phone) acc[p.promoter_id] = p.phone;
      return acc;
    }, {});
  }, [data]);

  // Client-side synthetic alerts for delayed promoters with no first check-in
  const clientAlerts = useMemo((): TeamAlert[] => {
    if (!data) return [];
    return data.promoters
      .filter((p) => p.status === 'delayed' && p.today_stats.first_checkin === null)
      .map((p) => ({
        id: `client-${p.promoter_id}`,
        type: 'NOT_STARTED',
        severity: 'HIGH',
        promoter_id: p.promoter_id,
        promoter_name: p.name,
        message: `${p.name} não iniciou a rota`,
        created_at: new Date().toISOString(),
        status: 'OPEN',
      }));
  }, [data]);

  const allAlerts = useMemo((): TeamAlert[] => {
    if (!data) return [];
    // Deduplicate: don't show a client-side alert if a real alert for the same promoter already exists
    const realPromoterIds = new Set(data.alerts.map((a) => a.promoter_id));
    const uniqueClientAlerts = clientAlerts.filter((a) => !realPromoterIds.has(a.promoter_id));
    return [...data.alerts, ...uniqueClientAlerts].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [data, clientAlerts]);

  const displayAlerts = useMemo(() => {
    if (tab === 'open') return allAlerts.filter((a) => a.status === 'OPEN');
    return allAlerts;
  }, [allAlerts, tab]);

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 space-y-3">
        <div className="h-10 bg-gray-200 rounded-xl animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-28 bg-gray-200 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const openCount = allAlerts.filter((a) => a.status === 'OPEN').length;

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-white px-4 pt-safe pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-gray-700" />
            <h1 className="text-lg font-semibold text-gray-900">Alertas</h1>
            {openCount > 0 && (
              <span className="ml-auto text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                {openCount} abertos
              </span>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex border-b border-gray-100 bg-white px-4">
          <button
            onClick={() => setTab('open')}
            className={`py-2.5 text-sm font-medium mr-6 border-b-2 transition-colors ${
              tab === 'open'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500'
            }`}
          >
            Abertos
          </button>
          <button
            onClick={() => setTab('all')}
            className={`py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'all'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500'
            }`}
          >
            Todos
          </button>
        </div>

        <div className="p-4 space-y-3">
          {displayAlerts.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
              <Bell size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Nenhum alerta{tab === 'open' ? ' aberto' : ''}</p>
              <p className="text-xs text-gray-400">Tudo em ordem por enquanto</p>
            </div>
          ) : (
            displayAlerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                phone={phoneMap[alert.promoter_id]}
                onReviewed={handleRefresh}
              />
            ))
          )}
        </div>
      </div>
    </PullToRefresh>
  );
}
